console.log("VPX ACHIEVEMENTS FILE LOADED");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Achievements module aborted.");
        return;
    }

    /*
    ==========================================
    Achievement System
    ------------------------------------------
    7 categories x 4 tiers (bronze/silver/gold/
    diamond) + 1 capstone = 29 achievements.
    Every category is computed from state that
    ALREADY exists (exerciseLogs, weightCheckIns,
    onboarding, blueprint.goal) — no new backend,
    no new user input required. The capstone
    ("VORYNEX ELITE") unlocks only when Diamond
    has been reached in all 7 categories — kept
    deliberately rare so it carries real social
    value when shared.
    ==========================================
    */

    const TIERS = ["bronze", "silver", "gold", "diamond"];
    const TIER_ICON = { bronze: "🥉", silver: "🥈", gold: "🥇", diamond: "💎", capstone: "👑" };

    /*
    ==========================================
    Rarity Stats (optional, backend-dependent)
    ------------------------------------------
    Shows "X% of members have this" next to
    unlocked badges — needs a /stats endpoint on
    the Cloudflare Worker that isn't built yet.
    Fails silently (returns null) if the endpoint
    is missing/unreachable, so the achievements
    gallery works perfectly with or without it.
    Fetched once per page load, cached, and
    triggers a single re-render when it arrives.
    ==========================================
    */

    let rarityCache = null;
    let rarityFetchStarted = false;

    function loadRarityStats() {
        if (rarityFetchStarted) return;
        rarityFetchStarted = true;

        const apiBase = window.VORYNEX.config && window.VORYNEX.config.apiBase;
        if (!apiBase) return;

        fetch(apiBase + "/stats")
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (data) {
                if (!data) return;
                rarityCache = data; // expected shape: { "streak_bronze": 42, "streak_silver": 18, ... } percentages
                if (window.VORYNEX.render && window.VORYNEX.render.render) {
                    window.VORYNEX.render.render();
                }
            })
            .catch(function () {
                // No /stats endpoint yet, or offline — rarity simply
                // won't show. Never surfaced as an error to the user.
            });
    }

    // Fire-and-forget: report a freshly unlocked achievement so the
    // backend can count it toward global rarity stats. Safe to call
    // even if the endpoint doesn't exist — errors are swallowed.
    function reportUnlock(achievementId) {
        const apiBase = window.VORYNEX.config && window.VORYNEX.config.apiBase;
        if (!apiBase) return;
        fetch(apiBase + "/track-unlock", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ achievementId: achievementId })
        }).catch(function () {});
    }

    const CATEGORIES = [
        { id: "streak", icon: "🔥", thresholds: [3, 7, 14, 30], unit: "days" },
        { id: "checkins", icon: "⚖️", thresholds: [2, 4, 8, 16], unit: "count" },
        { id: "progress", icon: "🎯", thresholds: [25, 50, 75, 100], unit: "percent" },
        { id: "workouts", icon: "💪", thresholds: [5, 20, 50, 100], unit: "count" },
        { id: "membership", icon: "📅", thresholds: [2, 4, 12, 26], unit: "weeks" },
        { id: "strengthPR", icon: "📈", thresholds: [5, 15, 30, 50], unit: "percent" },
        { id: "programs", icon: "🏁", thresholds: [1, 2, 3, 5], unit: "count" }
    ];

    function clamp(v, min, max) {
        return Math.min(max, Math.max(min, v));
    }

    function getSection(name, fallback) {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return fallback;
        const val = window.VORYNEX.state.getSection(name);
        return (val === null || val === undefined) ? fallback : val;
    }

    /*
    ==========================================
    Per-Category Value Calculators
    ------------------------------------------
    Each returns a single current numeric value,
    compared against CATEGORIES[i].thresholds to
    find the highest tier reached.
    ==========================================
    */

    function calcStreakDays() {
        const all = getSection("exerciseLogs", {});
        const dateSet = {};
        Object.keys(all).forEach(function (exId) {
            (all[exId] || []).forEach(function (entry) { dateSet[entry.date] = true; });
        });
        if (!Object.keys(dateSet).length) return 0;

        const cursor = new Date();
        cursor.setHours(0, 0, 0, 0);
        const todayStr = cursor.toISOString().slice(0, 10);
        if (!dateSet[todayStr]) cursor.setDate(cursor.getDate() - 1);

        let streak = 0;
        while (dateSet[cursor.toISOString().slice(0, 10)]) {
            streak++;
            cursor.setDate(cursor.getDate() - 1);
        }
        return streak;
    }

    function calcCheckIns() {
        const list = getSection("weightCheckIns", []);
        return list.length;
    }

    function calcProgressPercent() {
        const blueprint = getSection("blueprint", null);
        const onboarding = getSection("onboarding", null);
        const checkIns = getSection("weightCheckIns", []);

        if (!blueprint || !blueprint.goal) return 0;

        const target = blueprint.goal.targetWeight;
        const baseline = (onboarding && onboarding.answers && onboarding.answers.weightKg)
            ? onboarding.answers.weightKg
            : blueprint.goal.currentWeight;

        const sorted = checkIns.slice().sort(function (a, b) { return a.date < b.date ? -1 : 1; });
        const current = sorted.length ? sorted[sorted.length - 1].weightKg : baseline;

        if (target === baseline) return current === target ? 100 : 0;

        const totalDelta = target - baseline;
        const achievedDelta = current - baseline;
        const pct = (achievedDelta / totalDelta) * 100;
        return clamp(Math.round(pct), 0, 100);
    }

    function calcTotalWorkouts() {
        const all = getSection("exerciseLogs", {});
        let total = 0;
        Object.keys(all).forEach(function (exId) {
            total += (all[exId] || []).length;
        });
        return total;
    }

    function calcMembershipWeeks() {
        const ach = getSection("achievements", { unlocked: {}, firstSeenAt: null });
        let firstSeenAt = ach.firstSeenAt;

        if (!firstSeenAt) {
            // First time this module runs for this user — anchor "day
            // zero" now. No earlier signup timestamp exists anywhere
            // in state to fall back on, so this is the honest choice
            // for pre-existing accounts too (their clock starts today,
            // not retroactively).
            firstSeenAt = new Date().toISOString().slice(0, 10);
            ach.firstSeenAt = firstSeenAt;
            if (window.VORYNEX.state && window.VORYNEX.state.setSection) {
                window.VORYNEX.state.setSection("achievements", ach);
            }
        }

        const start = new Date(firstSeenAt);
        const now = new Date();
        const diffMs = now.getTime() - start.getTime();
        return Math.max(0, Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)));
    }

    function calcStrengthPR() {
        const all = getSection("exerciseLogs", {});
        let best = 0;

        Object.keys(all).forEach(function (exId) {
            const entries = (all[exId] || []).slice().sort(function (a, b) { return a.date < b.date ? -1 : 1; });
            if (entries.length < 2) return;
            const first = entries[0].weight;
            const last = entries[entries.length - 1].weight;
            if (!first || first <= 0) return;
            const pct = ((last - first) / first) * 100;
            if (pct > best) best = pct;
        });

        return Math.round(best);
    }

    function calcProgramsCompleted() {
        const onboarding = getSection("onboarding", null);
        const checkIns = getSection("weightCheckIns", []);
        const programWeeks = (onboarding && onboarding.answers && onboarding.answers.programWeeks) || 8;

        if (!checkIns.length) return 0;

        const isoWeeks = {};
        checkIns.forEach(function (c) {
            const d = new Date(c.date);
            const yearWeek = d.getUTCFullYear() + "-" + Math.ceil((((d - new Date(Date.UTC(d.getUTCFullYear(), 0, 1))) / 86400000) + 1) / 7);
            isoWeeks[yearWeek] = true;
        });

        const trackedWeeks = Object.keys(isoWeeks).length;
        return Math.floor(trackedWeeks / programWeeks);
    }

    const CALCULATORS = {
        streak: calcStreakDays,
        checkins: calcCheckIns,
        progress: calcProgressPercent,
        workouts: calcTotalWorkouts,
        membership: calcMembershipWeeks,
        strengthPR: calcStrengthPR,
        programs: calcProgramsCompleted
    };

    /*
    ==========================================
    Tier Resolution
    ==========================================
    */

    function tierForValue(thresholds, value) {
        let reached = -1;
        thresholds.forEach(function (t, i) {
            if (value >= t) reached = i;
        });
        return reached; // -1 = no tier yet, 0..3 = bronze..diamond index
    }

    /*
    ==========================================
    Public API
    ==========================================
    */

    const Achievements = {};

    // Returns { categories: [...], capstoneUnlocked: bool, newlyUnlocked: [...] }
    // Also persists newly-unlocked ids with today's date, so badges can
    // show "unlocked on [date]" and so newlyUnlocked can drive a one-time
    // celebration UI without re-triggering on every render.
    Achievements.evaluate = function () {

        loadRarityStats();

        const achState = getSection("achievements", { unlocked: {}, firstSeenAt: null });
        const alreadyUnlocked = achState.unlocked || {};
        const newlyUnlocked = [];
        let diamondCount = 0;

        const categories = CATEGORIES.map(function (cat) {

            const value = CALCULATORS[cat.id]();
            const tierIndex = tierForValue(cat.thresholds, value);
            const tierName = tierIndex >= 0 ? TIERS[tierIndex] : null;

            if (tierName === "diamond") diamondCount++;

            // Mark every tier up to and including the reached one as
            // unlocked (so Bronze/Silver badges persist even after the
            // person has moved on to Gold).
            const tiersUnlocked = [];
            for (let i = 0; i <= tierIndex; i++) {
                const id = cat.id + "_" + TIERS[i];
                tiersUnlocked.push(TIERS[i]);
                if (!alreadyUnlocked[id]) {
                    alreadyUnlocked[id] = new Date().toISOString().slice(0, 10);
                    newlyUnlocked.push(id);
                }
            }

            const nextThreshold = tierIndex < 3 ? cat.thresholds[tierIndex + 1] : null;

            return {
                id: cat.id,
                icon: cat.icon,
                unit: cat.unit,
                value: value,
                tierIndex: tierIndex,
                tierName: tierName,
                tiersUnlocked: tiersUnlocked,
                nextThreshold: nextThreshold,
                nameKey: "ach_cat_" + cat.id,
                tierNameKey: tierName ? "ach_tier_" + tierName : null
            };
        });

        const capstoneUnlocked = diamondCount === CATEGORIES.length;
        if (capstoneUnlocked && !alreadyUnlocked["vorynex_elite"]) {
            alreadyUnlocked["vorynex_elite"] = new Date().toISOString().slice(0, 10);
            newlyUnlocked.push("vorynex_elite");
        }

        if (window.VORYNEX.state && window.VORYNEX.state.setSection) {
            window.VORYNEX.state.setSection("achievements", { unlocked: alreadyUnlocked, firstSeenAt: achState.firstSeenAt });
        }

        newlyUnlocked.forEach(function (id) { reportUnlock(id); });

        return {
            categories: categories,
            capstoneUnlocked: capstoneUnlocked,
            capstoneIcon: TIER_ICON.capstone,
            unlockedDates: alreadyUnlocked,
            newlyUnlocked: newlyUnlocked,
            totalUnlockedCount: Object.keys(alreadyUnlocked).length
        };
    };

    Achievements.getTierIcon = function (tierName) {
        return TIER_ICON[tierName] || "";
    };

    // Returns a rounded percentage (0-100) or null if rarity data hasn't
    // loaded yet (no backend endpoint, offline, or still in flight).
    Achievements.getRarity = function (achievementId) {
        if (!rarityCache || rarityCache[achievementId] === undefined) return null;
        return Math.round(rarityCache[achievementId]);
    };

    Achievements.getCategories = function () {
        return CATEGORIES.slice();
    };

    window.VORYNEX.achievements = Achievements;
    console.log("Achievements module exported.", CATEGORIES.length, "categories x 4 tiers + capstone.");

})();